import {AsyncPipe, CurrencyPipe, NgClass} from '@angular/common';
import {Component, OnInit, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {EmTranslatePipe, LoggerService, PhonePipe, TranslationService} from '@emma-labs/shared/angular';
import {
  Agent,
  ArchivedPersonInsurance,
  getMonthlyPrice,
  InsuranceFirm,
  MinifiedProposal,
  MinifiedSaleReport,
  PersonInsurance,
  ProposalStatus,
  SaleReportStatus,
  trimPhoneNumber,
  UserRole,
} from '@emma-labs/shared/typescript';
import {MenuItem, SelectItem} from 'primeng/api';
import {CardModule} from 'primeng/card';
import {ContextMenuModule} from 'primeng/contextmenu';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {SkeletonModule} from 'primeng/skeleton';
import {Table, TableModule, TableRowSelectEvent} from 'primeng/table';
import {TagModule} from 'primeng/tag';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {AgentService} from '../../../services/agent.service';
import {AuthService} from '../../../services/auth.service';
import {ProposalService} from '../../../services/proposal/proposal.service';
import {SaleReportService} from '../../../services/sale/sale-report.service';
import {getProducts, Product, ProductType} from '../../../utils/product-type';
import {PROPOSALS_STATUS_OPTIONS} from '../proposals-status';
import {TableEntry} from './table-entry';

@Component({
  selector: 'em-old-table-section',
  templateUrl: './old-table-section.component.html',
  imports: [
    TableModule,
    CardModule,
    AsyncPipe,
    InputTextModule,
    SkeletonModule,
    CurrencyPipe,
    NgClass,
    PhonePipe,
    FormsModule,
    TagModule,
    MultiSelectModule,
    ContextMenuModule,
    EmTranslatePipe,
  ],
})
export class OldTableSectionComponent implements OnInit {
  @ViewChild('salesTable') salesTable!: Table;

  tableEntries$: Observable<TableEntry[]> = new Observable<TableEntry[]>();
  agent: Agent | undefined;
  selectedEntry: TableRowSelectEvent | undefined;
  contextMenuItems: MenuItem[] = [];
  matchModeOptions: SelectItem[] = [];
  readonly userRole = UserRole;
  readonly defaultRowsPerPage = 50;
  readonly productType = ProductType;
  readonly proposalsStatusOptions = PROPOSALS_STATUS_OPTIONS;

  constructor(
    private readonly translate: TranslationService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly proposalService: ProposalService,
    private readonly saleReportService: SaleReportService,
    private readonly agentService: AgentService,
    private readonly logger: LoggerService,
  ) {
    this.tableEntries$ = this.getTableEntries();
  }

  ngOnInit(): void {
    this.contextMenuItems = [
      {
        label: this.translate.currentLanguageIsFr()
          ? 'Ouvrir le lien dans un nouvel onglet'
          : 'Open link in new tab',
        icon: 'pi pi-external-link',
        command: () => this.onRowSelect({data: this.selectedEntry}, true),
      },
    ];

    this.matchModeOptions = [
      {label: this.translate.currentLanguageIsFr() ? 'Contient' : 'Contains', value: 'contains'},
      {
        label: this.translate.currentLanguageIsFr() ? 'Ne contient pas' : 'Not contains',
        value: 'notContains',
      },
    ];
  }

  applyFilterGlobal($event: Event): void {
    let value = ($event.target as HTMLInputElement).value.trim();
    value = this.handlePhoneNumberCase(value);
    this.salesTable.filterGlobal(value, 'contains');
  }

  getStatusSeverity(status: ProposalStatus): 'success' | 'danger' | 'warning' | 'info' | 'secondary' {
    switch (status) {
      case ProposalStatus.APPROVED:
        return 'success';
      case ProposalStatus.REFUSED:
      case ProposalStatus.ABANDONED:
        return 'danger';
      case ProposalStatus.PENDING_UNDERWRITING:
      case ProposalStatus.PENDING_EXTERNAL_ACTION:
        return 'secondary';
      case ProposalStatus.PENDING_MODIFICATION_DECISION:
      case ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES:
        return 'warning';
      default:
        return 'info';
    }
  }

  sortStatus(event: {data: TableEntry[]; order: number}): void {
    const order = [
      ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES,
      ProposalStatus.PENDING_MODIFICATION_DECISION,
      ProposalStatus.PENDING_UNDERWRITING,
      ProposalStatus.PENDING_CHECKOUT,
      ProposalStatus.ONGOING,
      ProposalStatus.APPROVED,
      ProposalStatus.REFUSED,
      ProposalStatus.ABANDONED,
    ];

    event.data.sort((data1: TableEntry, data2: TableEntry) => {
      const value1 = order.indexOf(data1.status!.value);
      const value2 = order.indexOf(data2.status!.value);
      return event.order * (value1 - value2);
    });
  }

  onRowSelect($event: TableRowSelectEvent, targetBlank = false): void {
    let url: string;

    switch ($event.data.status.value) {
      case ProposalStatus.ONGOING:
      case ProposalStatus.PENDING_CHECKOUT:
        url = `/${['dashboard', 'proposal', btoa($event.data.proposalId)].join('/')}`;
        break;

      case ProposalStatus.APPROVED:
      case ProposalStatus.REFUSED:
      case ProposalStatus.ABANDONED:
      case ProposalStatus.PENDING_UNDERWRITING:
      case ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES:
      case ProposalStatus.PENDING_EXTERNAL_ACTION:
      case ProposalStatus.PENDING_MODIFICATION_DECISION:
        url = `/${['dashboard', 'archived-proposal', btoa($event.data.proposalId)].join('/')}`;
        break;

      default:
        return;
    }

    targetBlank ? window.open(url, '_blank') : this.router.navigate([url]);
  }

  private getTableEntries(): Observable<TableEntry[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      switchMap((session) => {
        this.agent = session.agent;

        return forkJoin([
          this.proposalService.getAll(true),
          this.saleReportService.getAll(session.agent.role === UserRole.ADVISOR, true, true),
        ]);
      }),
      first(),
      switchMap(([proposals, saleReports]) => {
        const agentIds = new Set<string>(
          proposals
            .flatMap((p) => p.agents!.filter((a) => !(a.agentId in InsuranceFirm)))
            .map((a) => a.agentId),
        );

        return forkJoin([
          this.agentService.getAgentsFromIds([...agentIds]).pipe(first()),
          of(proposals),
          of(saleReports),
        ]);
      }),
      map(([agents, proposals, saleReports]) => {
        const agentNamesMap = new Map<string, string>();
        agents.forEach((a) => agentNamesMap.set(a.id, a.fullName));

        return this.mapToTableEntries(proposals, saleReports, agentNamesMap);
      }),
    );
  }

  private mapToTableEntries(
    proposals: MinifiedProposal[],
    saleReports: MinifiedSaleReport[],
    agentNamesMap: Map<string, string>,
  ): TableEntry[] {
    const proposalTableEntries =
      proposals && proposals.length > 0
        ? proposals.map((proposal) => {
            const primaryAgent = proposal.agents?.find((a) => a.isPrimary);

            if (!primaryAgent) {
              throw new Error('A proposal should always have a primary agent.');
            }

            const status = this.getProposalStatusFromProposal(proposal);

            const proposalTableEntry: TableEntry = {
              agent: agentNamesMap.get(primaryAgent.agentId),
              proposalId: proposal.id,
              fullName: `${proposal.submitter?.firstName ?? ''} ${proposal.submitter?.lastName ?? ''}`,
              phone: proposal.submitter?.phone,
              status: {text: this.translate.getTranslation(status)!, value: status},
              clientId: proposal.userId,
              products: proposal.protection
                ? this.getTranslatedProducts(proposal.protection.personInsurances)
                : [],
              monthlyPrice: proposal.protection ? getMonthlyPrice(proposal.protection.personInsurances) : 0,
            };

            return proposalTableEntry;
          })
        : [];

    const saleReportTableEntries =
      saleReports && saleReports.length > 0
        ? saleReports.map((saleReport) => {
            const primaryAgent = saleReport.proposal.agents.find((agent) => agent.isPrimary);

            if (!primaryAgent) {
              throw new Error('No primary agent found for sale report');
            }

            const status = this.getProposalStatusFromSaleReport(saleReport);

            const saleReportTableEntry: TableEntry = {
              agent: `${primaryAgent.firstName} ${primaryAgent.lastName}`,
              policyNumber: saleReport.policyNumber,
              proposalId: saleReport.proposal.id,
              fullName: `${saleReport.proposal.submitter.firstName} ${saleReport.proposal.submitter.lastName}`,
              phone: saleReport.proposal.submitter.phone,
              status: {text: this.translate.getTranslation(status)!, value: status},
              clientId: saleReport.userId,
              products: this.getTranslatedProducts(saleReport.protection.personInsurances),
              monthlyPrice: getMonthlyPrice(saleReport.protection.personInsurances),
            };

            return saleReportTableEntry;
          })
        : [];

    return [...proposalTableEntries, ...saleReportTableEntries];
  }

  private getProposalStatusFromProposal(proposal: MinifiedProposal): ProposalStatus {
    if (!proposal.extra?.sentAt) {
      return ProposalStatus.ONGOING;
    }

    if (!proposal.submittedAt) {
      return ProposalStatus.PENDING_CHECKOUT;
    }

    this.logger.warn('Proposal status not found', {proposalId: proposal.id});
    return ProposalStatus.ONGOING;
  }

  private getProposalStatusFromSaleReport(saleReport: MinifiedSaleReport): ProposalStatus {
    if (
      saleReport.status === SaleReportStatus.APPROVED_BY_INSURER &&
      saleReport.modificationNotice &&
      !saleReport.modificationDecisionMadeAt
    ) {
      return ProposalStatus.PENDING_MODIFICATION_DECISION;
    }

    if (
      saleReport.status === SaleReportStatus.APPROVED_BY_INSURER ||
      saleReport.status === SaleReportStatus.IN_FORCE
    ) {
      return ProposalStatus.APPROVED;
    }

    if (
      saleReport.status === SaleReportStatus.REFUSED_BY_INSURER ||
      saleReport.status === SaleReportStatus.REFUSED_BY_CLIENT
    ) {
      return ProposalStatus.REFUSED;
    }

    if (saleReport.status === SaleReportStatus.ABANDONED) {
      return ProposalStatus.ABANDONED;
    }

    const eqsCompleted = saleReport.proposal.healthAnswersRevisions?.every((har) => har.completedAt) ?? true;
    if (!eqsCompleted) {
      return ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES;
    }

    if (saleReport.status === SaleReportStatus.UNDERWRITING) {
      if (saleReport.proposal.underwritingProfile?.extraRequirementsFile) {
        const {hasDocument, extraRequirementsInquiries} =
          saleReport.proposal.underwritingProfile.extraRequirementsFile;
        return extraRequirementsInquiries.length && !hasDocument
          ? ProposalStatus.PENDING_EXTERNAL_ACTION
          : ProposalStatus.PENDING_UNDERWRITING;
      }
      return ProposalStatus.PENDING_UNDERWRITING;
    }

    this.logger.warn('Proposal status not found', {
      saleReportId: saleReport.id,
      saleReportStatus: saleReport.status,
    });
    return ProposalStatus.ONGOING;
  }

  private getTranslatedProducts(
    personInsurances:
      | Pick<PersonInsurance, 'duration' | 'riders'>[]
      | Pick<ArchivedPersonInsurance, 'duration' | 'riders'>[],
  ): Product[] {
    return getProducts(personInsurances).map((product) => ({
      ...product,
      name:
        product.type !== ProductType.RIDER
          ? product.name
          : (this.translate.getTranslation(product.name) ?? ''),
    }));
  }

  private handlePhoneNumberCase(value: string): string {
    if (/(\+1|\(|\)|-)/.test(value)) {
      value = trimPhoneNumber(value);
    }
    return value;
  }
}
